import React, { useState } from 'react';
import { Box, Breadcrumbs, Button, CircularProgress, Dialog, Grid, Link, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { deleteContest, publishContest } from '../../../redux/contest/actions';
import { useDispatchPromise } from '../../../utils/AppUtils';
import { DialogueSheet } from '../../../components/DialogueSheet/DialogueSheet';
import type { IContest } from '../../../types/global/helper';
import type { IContestManageResponse } from '../../../redux/contest/types';
import { ROUTES } from '../../../types/global/routes.types';
import { useThemeContext } from '../../../hook/useThemeManager';
import { theme } from '../../../constants/theme';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { ContestFundingStep } from '../contestWizard/steps/ContestFundingStep';
import { BRAND_LUXE_PRIMARY } from '../brandDashboardTokens';
import { midnight } from '../contestWizard/midnightStyles';
import { BrandContestEditorialView } from './BrandContestEditorialView';
import { BrandContestManageHub, BrandContestManageHandlers } from './BrandContestManageHub';

function BrandContestBreadcrumbs({ contestTitle }: { contestTitle?: string }) {
    const { themeStyle } = useThemeContext();
    const t = theme[themeStyle];

    return (
        <Breadcrumbs
            aria-label="breadcrumb"
            sx={{
                alignSelf: 'flex-start',
                width: '100%',
                maxWidth: '100%',
                '& .MuiBreadcrumbs-ol': {
                    flexWrap: 'nowrap',
                    whiteSpace: 'nowrap',
                },
                '& .MuiBreadcrumbs-li': {
                    minWidth: 0,
                    flexShrink: 0,
                    '&:last-of-type': {
                        flexShrink: 1,
                        minWidth: 0,
                        overflow: 'hidden',
                    },
                },
                '& .MuiBreadcrumbs-separator': {
                    color: t.form_label,
                    flexShrink: 0,
                },
            }}>
            <Link
                component={RouterLink}
                to={ROUTES.BRAND_CAMPAIGNS}
                underline="hover"
                sx={{
                    fontFamily: FONT_ENUM.MAIN_BOLD,
                    fontSize: 13,
                    color: t.primary_white,
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    '&:hover': { color: BRAND_LUXE_PRIMARY },
                }}>
                Campaigns
            </Link>
            {contestTitle ? (
                <Link
                    component={RouterLink}
                    to={`${ROUTES.BRAND_CAMPAIGNS}?tab=contest`}
                    underline="hover"
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: 13,
                        color: t.primary_white,
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                        '&:hover': { color: BRAND_LUXE_PRIMARY },
                    }}>
                    Contests
                </Link>
            ) : (
                <Typography
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: 13,
                        color: BRAND_LUXE_PRIMARY,
                        whiteSpace: 'nowrap',
                    }}>
                    Contests
                </Typography>
            )}
            {contestTitle ? (
                <Typography
                    component="span"
                    title={contestTitle}
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: 13,
                        color: BRAND_LUXE_PRIMARY,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        minWidth: 0,
                    }}>
                    {contestTitle}
                </Typography>
            ) : null}
        </Breadcrumbs>
    );
}

interface Props {
    contest: IContest | null;
    managedContest: IContestManageResponse | null;
    loading: boolean;
    error: unknown;
    onClearError: () => void;
    onRefresh: () => void;
    contestManageHandlers?: BrandContestManageHandlers;
}

export function BrandMidnightContestManageContainer({
    contest,
    managedContest,
    loading,
    error,
    onClearError,
    onRefresh,
    contestManageHandlers,
}: Props) {
    const dispatch = useDispatch();
    const dispatchPromise = useDispatchPromise();
    const navigate = useNavigate();
    const { themeStyle } = useThemeContext();

    const access = managedContest?.access;
    const canSeeSalesTotals = access ? access.can_see_sales_totals !== false : true;
    const canPublish = access ? access.can_publish !== false : true;
    const canUseWallet = access ? access.can_use_wallet === true : true;
    const canDelete = access ? access.can_delete === true : true;
    const [fundDialogOpen, setFundDialogOpen] = useState(false);
    const [fundingRefreshKey, setFundingRefreshKey] = useState(0);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pb: 4 }}>
                <Box
                    sx={{
                        maxWidth: 1280,
                        mx: 'auto',
                        px: { xs: 2, md: 3 },
                        pt: 1,
                        width: '100%',
                        minWidth: 0,
                        overflow: 'hidden',
                    }}>
                    <BrandContestBreadcrumbs />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 12 }}>
                    <CircularProgress sx={{ color: BRAND_LUXE_PRIMARY }} />
                </Box>
            </Box>
        );
    }

    if (error || !contest || !managedContest) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pb: 4 }}>
                <Box
                    sx={{
                        maxWidth: 1280,
                        mx: 'auto',
                        px: { xs: 2, md: 3 },
                        pt: 1,
                        width: '100%',
                        minWidth: 0,
                        overflow: 'hidden',
                    }}>
                    <BrandContestBreadcrumbs />
                </Box>
                <Box sx={{ textAlign: 'center', py: 8, px: 3 }}>
                    <Typography sx={{ color: midnight.onSurface, mb: 2, fontWeight: 700 }}>
                        Could not load campaign
                    </Typography>
                    <Button onClick={onClearError} sx={{ color: midnight.primary, fontWeight: 700 }}>
                        Retry
                    </Button>
                </Box>
            </Box>
        );
    }

    const submissions = managedContest.submissions;
    const sales = managedContest.sales;
    const votes = managedContest.votes;

    const closeFundDialog = () => {
        setFundDialogOpen(false);
        setFundingRefreshKey(k => k + 1);
        onRefresh();
    };

    const handlePublish = () => {
        dispatch(publishContest({ documentId: contest._id }));
    };

    const submissionCount = submissions.pagination.totalCount || 0;
    const deleteMessage =
        submissionCount > 0
            ? `${contest.title} has ${submissionCount} ${
                  submissionCount === 1 ? 'submission' : 'submissions'
              }. Are you sure you want to delete this contest? This action cannot be undone.`
            : contest.status === 'published'
            ? `${contest.title} is currently live. Are you sure you want to delete this contest? This action cannot be undone.`
            : 'You are about to delete this contest and this action cannot be reversed.';

    const handleConfirmDelete = () => {
        void dispatchPromise(
            deleteContest({
                documentId: contest._id,
                payload: { slug: contest.slug || contest.contest_id },
            }),
        )
            .then(() => {
                setDeleteDialogOpen(false);
                navigate(`${ROUTES.BRAND_CAMPAIGNS}?tab=contest`);
            })
            .catch(() => {
                setDeleteDialogOpen(false);
            });
    };

    return (
        <>
            <Box
                sx={{
                    maxWidth: 1280,
                    mx: 'auto',
                    px: { xs: 2, md: 3 },
                    pt: 1,
                    pb: 2,
                    width: '100%',
                    minWidth: 0,
                    overflow: 'hidden',
                }}>
                <BrandContestBreadcrumbs contestTitle={contest.title} />
            </Box>
            <BrandContestEditorialView
                contest={contest}
                fundingRefreshKey={fundingRefreshKey}
                submissionCount={submissions.pagination.totalCount || 0}
                salesFormatted={canSeeSalesTotals ? sales.total?.formatted_amount || '$0.00' : 'xxxxx'}
                voteCount={votes.total || 0}
                onFundClick={() => setFundDialogOpen(true)}
                onPublishClick={handlePublish}
                showFundButton={canUseWallet}
                hideFinances={!canSeeSalesTotals}
                showPublishButton={canPublish && Boolean(contest.funded && contest.status !== 'published')}
                onFeatureImageChange={
                    contestManageHandlers
                        ? (_preview, file) => contestManageHandlers.onSaveChanges({ feature_image: [file] })
                        : undefined
                }
                manageHub={
                    contestManageHandlers ? (
                        <BrandContestManageHub
                            contest={contest}
                            managedContest={managedContest}
                            handlers={contestManageHandlers}
                            onAddFunds={() => setFundDialogOpen(true)}
                            fundingRefreshKey={fundingRefreshKey}
                        />
                    ) : undefined
                }
            />

            <Box
                sx={{
                    maxWidth: 1280,
                    mx: 'auto',
                    px: { xs: 2, md: 3 },
                    pb: 4,
                }}>
                <Box
                    component="section"
                    data-purpose="danger-zone-section"
                    sx={{
                        position: 'relative',
                        overflow: 'hidden',
                        mt: 6,
                        p: { xs: 3, md: 5 },
                        borderRadius: 3,
                        border: `1px solid ${alpha(theme[themeStyle].primary_red, 0.3)}`,
                        bgcolor: 'rgba(14,14,14,0.8)',
                        backdropFilter: 'blur(16px)',
                        transition: 'box-shadow 0.25s ease, border-color 0.25s ease',
                        '&:hover': {
                            boxShadow: `0 0 24px ${alpha(theme[themeStyle].primary_red, 0.14)}`,
                            borderColor: alpha(theme[themeStyle].primary_red, 0.4),
                        },
                    }}>
                    <Box
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            width: '100%',
                            height: '100%',
                            borderRadius: '50%',
                            bgcolor: alpha(theme[themeStyle].primary_red, 0.04),
                            filter: 'blur(72px)',
                            pointerEvents: 'none',
                        }}
                    />
                    <Grid container spacing={4} alignItems="center" sx={{ position: 'relative', zIndex: 1 }}>
                        <Grid item xs={12} md={8}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <Box
                                        sx={{
                                            width: 48,
                                            height: 48,
                                            borderRadius: '50%',
                                            border: `1px solid ${alpha(theme[themeStyle].primary_red, 0.3)}`,
                                            bgcolor: alpha(theme[themeStyle].primary_red, 0.1),
                                            color: theme[themeStyle].primary_red,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}>
                                        <ReportProblemOutlinedIcon sx={{ fontSize: 24 }} />
                                    </Box>
                                    <Typography
                                        sx={{
                                            color: theme[themeStyle].primary_red,
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontSize: { xs: '1.45rem', md: '1.65rem' },
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.26em',
                                        }}>
                                        Danger Zone
                                    </Typography>
                                </Box>

                                <Box sx={{ maxWidth: '42rem' }}>
                                    <Typography
                                        sx={{
                                            color: theme[themeStyle].tag_white,
                                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                                            fontSize: '0.88rem',
                                            lineHeight: 1.58,
                                        }}>
                                        {submissionCount > 0 ? (
                                            <>
                                                This contest has {submissionCount}{' '}
                                                {submissionCount === 1 ? 'submission' : 'submissions'}. Deleting it will
                                                remove all associated entries. Once you delete a contest, this action
                                                cannot be undone. All associated submission data, jury scores, and award
                                                history will be permanently erased.
                                            </>
                                        ) : (
                                            <>
                                                Once you delete a contest, this action cannot be undone. All associated
                                                submission data, jury scores, and award history will be permanently
                                                erased.
                                            </>
                                        )}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            color: theme[themeStyle].subtitleGrey,
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontSize: '0.74rem',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.12em',
                                            mt: 1.5,
                                        }}>
                                        Please be absolutely certain before proceeding.
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>

                        <Grid item xs={12} md={4}>
                            {canDelete ? (
                                <Box sx={{ display: 'flex', justifyContent: { xs: 'stretch', md: 'flex-end' } }}>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        data-purpose="delete-contest-button"
                                        onClick={() => setDeleteDialogOpen(true)}
                                        sx={{
                                            px: 4,
                                            py: 1.65,
                                            width: { xs: '100%', md: 'auto' },
                                            bgcolor: theme[themeStyle].primary_red,
                                            color: theme[themeStyle].primary_white,
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontSize: '0.8rem',
                                            letterSpacing: '0.08em',
                                            textTransform: 'uppercase',
                                            borderRadius: 2.2,
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            boxShadow: `0 10px 15px -3px ${alpha(theme[themeStyle].primary_red, 0.2)}`,
                                            '&:hover': {
                                                bgcolor: alpha(theme[themeStyle].primary_red, 0.85),
                                                boxShadow: `0 10px 15px -3px ${alpha(
                                                    theme[themeStyle].primary_red,
                                                    0.25,
                                                )}`,
                                            },
                                            '&:focus-visible': {
                                                outline: 'none',
                                                boxShadow: `0 0 0 4px ${alpha(theme[themeStyle].primary_red, 0.35)}`,
                                            },
                                        }}>
                                        <DeleteForeverOutlinedIcon sx={{ fontSize: 18 }} />
                                        Delete Contest
                                    </Button>
                                </Box>
                            ) : null}
                        </Grid>
                    </Grid>
                </Box>
            </Box>

            <Box sx={{ maxWidth: 1280, mx: 'auto', px: { xs: 2, md: 3 }, pb: 6, textAlign: 'center' }}>
                {/* Navigation disabled until brand full manager route is ready */}
                <Typography
                    component="span"
                    sx={{
                        color: midnight.onSurfaceVariant,
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: 'default',
                    }}>
                    Open full manager (submissions, awards, analytics…)
                </Typography>
                {/* <Link
                    component={RouterLink}
                    to={replaceRouteParam(ROUTES.MANAGE_CONTEST, 'contestId', contest.slug || contest._id)}
                    sx={{
                        color: midnight.onSurfaceVariant,
                        fontSize: 13,
                        fontWeight: 600,
                        textDecoration: 'none',
                        '&:hover': { color: midnight.primary },
                    }}>
                    Open full manager (submissions, awards, analytics…)
                </Link> */}
            </Box>

            <Dialog
                open={fundDialogOpen}
                onClose={closeFundDialog}
                fullWidth
                maxWidth="lg"
                PaperProps={{
                    sx: {
                        bgcolor: midnight.surface,
                        backgroundImage: 'none',
                        borderRadius: '24px',
                        border: '1px solid rgba(255,255,255,0.08)',
                        maxHeight: '90vh',
                    },
                }}>
                <Box sx={{ p: { xs: 2, md: 3 }, overflow: 'auto' }}>
                    <ContestFundingStep contestId={contest._id} contestTitle={contest.title} expectCashPrizes />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                        <Button onClick={closeFundDialog} sx={{ color: midnight.primary, fontWeight: 700 }}>
                            Close
                        </Button>
                    </Box>
                </Box>
            </Dialog>

            <DialogueSheet
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                onAgreeClicked={handleConfirmDelete}
                onDisagreeClick={() => setDeleteDialogOpen(false)}
                header="Delete Contest?"
                body={deleteMessage}
                positiveText="Delete"
                negativeText="Cancel"
            />
        </>
    );
}
